import React, { useState } from 'react';
import { X, Users, UserCheck, Building, Mail, Phone, Plus, Edit, Trash2, Search } from 'lucide-react';
import { useUsers } from '../contexts/UserContext';

const UsersListModal = ({ isOpen, onClose }) => {
  const { users, deleteUser, updateUser } = useUsers();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    department: ''
  });

  if (!isOpen) return null;

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.designation && user.designation.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (user.specialization && user.specialization.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      department: user.department
    });
  };

  const handleSaveEdit = () => {
    if (editingUser) {
      updateUser(editingUser.id, editForm);
      setEditingUser(null);
      setEditForm({ name: '', email: '', role: '', department: '' });
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({ name: '', email: '', role: '', department: '' });
  };

  const handleDelete = (userId) => {
    deleteUser(userId);
    setShowDeleteConfirm(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#dc2626';
      case 'employee': return '#059669';
      default: return '#6b7280';
    }
  };

  const getDepartmentColor = (department) => {
    const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#f97316'];
    const hash = department.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="modal-overlay">
      <div className="modal users-list-modal">
        <div className="modal-header">
          <div className="modal-title">
            <Users size={20} style={{ marginRight: '8px' }} />
            All Users ({users.length})
          </div>
          <button onClick={onClose} className="btn-icon">
            <X size={16} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="users-header">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search users by name, email, role, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="users-stats">
              <div className="stat-item">
                <span className="stat-label">Total:</span>
                <span className="stat-value">{users.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Active:</span>
                <span className="stat-value">{users.filter(u => u.isActive).length}</span>
              </div>
            </div>
          </div>

          {filteredUsers.length > 0 ? (
            <div className="users-list">
              {filteredUsers.map((user) => (
                <div key={user.id} className="user-item">
                  {editingUser?.id === user.id ? (
                    <div className="edit-form">
                      <div className="form-row">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                          placeholder="Name"
                          className="form-input"
                        />
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          placeholder="Email"
                          className="form-input"
                        />
                      </div>
                      <div className="form-row">
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                          className="form-select"
                        >
                          <option value="employee">Employee</option>
                          <option value="admin">Admin</option>
                        </select>
                        <input
                          type="text"
                          value={editForm.department}
                          onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                          placeholder="Department"
                          className="form-input"
                        />
                      </div>
                      <div className="form-actions">
                        <button onClick={handleSaveEdit} className="btn btn-primary btn-sm">
                          Save
                        </button>
                        <button onClick={handleCancelEdit} className="btn btn-secondary btn-sm">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="user-avatar">
                        <UserCheck size={20} color={getRoleColor(user.role)} />
                      </div>
                      <div className="user-details">
                        <div className="user-name">{user.name}</div>
                        <div className="user-email">
                          <Mail size={14} />
                          {user.email}
                        </div>
                        <div className="user-meta">
                          <span 
                            className="role-badge"
                            style={{ backgroundColor: getRoleColor(user.role) + '20', color: getRoleColor(user.role) }}
                          >
                            {user.role}
                          </span>
                          <span 
                            className="department-badge"
                            style={{ backgroundColor: getDepartmentColor(user.department) + '20', color: getDepartmentColor(user.department) }}
                          >
                            <Building size={12} />
                            {user.department}
                          </span>
                        </div>
                        {user.designation && (
                          <div className="user-designation">
                            <span className="designation-label">Designation:</span>
                            <span className="designation-value">{user.designation}</span>
                          </div>
                        )}
                        {user.specialization && (
                          <div className="user-specialization">
                            <span className="specialization-label">Specialization:</span>
                            <span className="specialization-value">{user.specialization}</span>
                          </div>
                        )}
                      </div>
                      <div className="user-actions">
                        <button
                          onClick={() => handleEdit(user)}
                          className="btn-icon"
                          title="Edit User"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(user.id)}
                          className="btn-icon btn-danger"
                          title="Delete User"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-users">
              <Users size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Users Found</h3>
              <p className="text-gray-500">
                {searchQuery ? 'No users match your search criteria.' : 'No users have been added yet.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-icon">
                <X size={16} />
              </button>
            </div>
            <div className="modal-content">
              <p>Are you sure you want to delete this user? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="btn btn-danger">
                <Trash2 size={16} style={{ marginRight: '8px' }} />
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersListModal;
